import {
  Box,
  Group,
  ActionIcon,
  Slider,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { useRef, useState, useEffect, forwardRef } from 'react';
import {
  IconPlayerPlay,
  IconPlayerPause,
  IconPlayerSkipBack,
  IconPlayerSkipForward,
  IconVolume,
  IconVolume2,
  IconVolume3,
  IconVolumeOff,
  IconMaximize,
  IconMinimize,
  IconSettings,
  IconPictureInPicture,
  IconRepeat,
  IconRepeatOff,
} from '@tabler/icons-react';
import { Paper } from '@/components/ui';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export interface MediaPlayerProps {
  src: string;
  type?: 'video' | 'audio';
  poster?: string;
  title?: string;
  autoPlay?: boolean;
  loop?: boolean;
  muted?: boolean;
  controls?: boolean;
  width?: number | string;
  height?: number | string;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
  gradientVariant?: 'primary' | 'secondary' | 'tertiary';
}

const formatTime = (seconds: number): string => {
  if (isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const MediaPlayer = forwardRef<HTMLDivElement, MediaPlayerProps>(
  (
    {
      src,
      type = 'video',
      poster,
      title,
      autoPlay = false,
      loop = false,
      muted = false,
      controls = true,
      width = '100%',
      height = 'auto',
      onPlay,
      onPause,
      onEnded,
      onTimeUpdate,
      gradientVariant = 'primary',
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const gradient = theme.other?.gradients?.[gradientVariant];
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const mediaRef = useRef<HTMLVideoElement | HTMLAudioElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(muted);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isLooping, setIsLooping] = useState(loop);
    const [showControls, setShowControls] = useState(true);
    const [isBuffering, setIsBuffering] = useState(false);

    useEffect(() => {
      const media = mediaRef.current;
      if (!media) return;

      const handleLoadedMetadata = () => setDuration(media.duration);
      const handleTimeUpdate = () => {
        setCurrentTime(media.currentTime);
        onTimeUpdate?.(media.currentTime, media.duration);
      };
      const handlePlay = () => {
        setIsPlaying(true);
        onPlay?.();
      };
      const handlePause = () => {
        setIsPlaying(false);
        onPause?.();
      };
      const handleEnded = () => {
        setIsPlaying(false);
        onEnded?.();
      };
      const handleWaiting = () => setIsBuffering(true);
      const handlePlaying = () => setIsBuffering(false);

      media.addEventListener('loadedmetadata', handleLoadedMetadata);
      media.addEventListener('timeupdate', handleTimeUpdate);
      media.addEventListener('play', handlePlay);
      media.addEventListener('pause', handlePause);
      media.addEventListener('ended', handleEnded);
      media.addEventListener('waiting', handleWaiting);
      media.addEventListener('playing', handlePlaying);

      return () => {
        media.removeEventListener('loadedmetadata', handleLoadedMetadata);
        media.removeEventListener('timeupdate', handleTimeUpdate);
        media.removeEventListener('play', handlePlay);
        media.removeEventListener('pause', handlePause);
        media.removeEventListener('ended', handleEnded);
        media.removeEventListener('waiting', handleWaiting);
        media.removeEventListener('playing', handlePlaying);
      };
    }, [onPlay, onPause, onEnded, onTimeUpdate]);

    const togglePlay = () => {
      const media = mediaRef.current;
      if (!media) return;

      if (isPlaying) {
        media.pause();
      } else {
        media.play();
      }
    };

    const handleSeek = (value: number) => {
      const media = mediaRef.current;
      if (!media) return;
      media.currentTime = value;
      setCurrentTime(value);
    };

    const handleVolumeChange = (value: number) => {
      const media = mediaRef.current;
      if (!media) return;
      media.volume = value;
      setVolume(value);
      setIsMuted(value === 0);
    };

    const toggleMute = () => {
      const media = mediaRef.current;
      if (!media) return;

      if (isMuted) {
        media.muted = false;
        media.volume = volume || 1;
        setIsMuted(false);
      } else {
        media.muted = true;
        setIsMuted(true);
      }
    };

    const toggleFullscreen = async () => {
      if (!containerRef.current) return;

      if (!isFullscreen) {
        await containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    };

    const toggleLoop = () => {
      const media = mediaRef.current;
      if (!media) return;
      media.loop = !isLooping;
      setIsLooping(!isLooping);
    };

    const skip = (seconds: number) => {
      const media = mediaRef.current;
      if (!media) return;
      media.currentTime = Math.max(0, Math.min(duration, currentTime + seconds));
    };

    const togglePiP = async () => {
      const video = mediaRef.current as HTMLVideoElement;
      if (!video || type !== 'video') return;

      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else {
        await video.requestPictureInPicture();
      }
    };

    const VolumeIcon = isMuted ? IconVolumeOff : volume < 0.3 ? IconVolume3 : volume < 0.7 ? IconVolume2 : IconVolume;

    const renderMedia = () => {
      if (type === 'video') {
        return (
          <video
            ref={mediaRef as React.RefObject<HTMLVideoElement>}
            src={src}
            poster={poster}
            autoPlay={autoPlay}
            loop={isLooping}
            muted={isMuted}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            onClick={togglePlay}
          />
        );
      }

      return (
        <audio
          ref={mediaRef as React.RefObject<HTMLAudioElement>}
          src={src}
          autoPlay={autoPlay}
          loop={isLooping}
          muted={isMuted}
        />
      );
    };

    return (
      <Paper
        ref={ref}
        variant="elevated"
        p={0}
        style={{ overflow: 'hidden', width, height }}
      >
        <Box
          ref={containerRef}
          pos="relative"
          style={{ backgroundColor: '#000' }}
          onMouseEnter={() => setShowControls(true)}
          onMouseLeave={() => setShowControls(!isPlaying)}
        >
          {renderMedia()}

          {controls && (
            <Box
              pos="absolute"
              bottom={0}
              left={0}
              right={0}
              p="sm"
              style={{
                background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                opacity: showControls ? 1 : 0,
                transition: 'opacity 0.3s ease',
              }}
            >
              {/* Progress bar */}
              <Slider
                value={currentTime}
                onChange={handleSeek}
                max={duration || 100}
                size="xs"
                mb="xs"
                color={gradient?.from}
                styles={{
                  track: { cursor: 'pointer' },
                  thumb: { display: 'none' },
                  bar: {
                    backgroundImage: `linear-gradient(90deg, ${gradient?.from}, ${gradient?.to})`,
                  },
                }}
              />

              <Group justify="space-between">
                <Group gap="xs">
                  {/* Play/Pause */}
                  <ActionIcon variant="subtle" c="white" onClick={togglePlay} size="lg">
                    {isPlaying ? <IconPlayerPause size={20} /> : <IconPlayerPlay size={20} />}
                  </ActionIcon>

                  {/* Skip backward */}
                  <ActionIcon variant="subtle" c="white" onClick={() => skip(-10)}>
                    <IconPlayerSkipBack size={18} />
                  </ActionIcon>

                  {/* Skip forward */}
                  <ActionIcon variant="subtle" c="white" onClick={() => skip(10)}>
                    <IconPlayerSkipForward size={18} />
                  </ActionIcon>

                  {/* Volume */}
                  <Group gap={4}>
                    <ActionIcon variant="subtle" c="white" onClick={toggleMute}>
                      <VolumeIcon size={18} />
                    </ActionIcon>
                    <Slider
                      value={isMuted ? 0 : volume}
                      onChange={handleVolumeChange}
                      max={1}
                      step={0.1}
                      w={80}
                      size="xs"
                      color={gradient?.from}
                    />
                  </Group>

                  {/* Time */}
                  <Text size="xs" c="white" style={accessibilityStyles}>
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </Text>
                </Group>

                <Group gap="xs">
                  {/* Loop */}
                  <ActionIcon variant="subtle" c="white" onClick={toggleLoop}>
                    {isLooping ? <IconRepeat size={18} /> : <IconRepeatOff size={18} />}
                  </ActionIcon>

                  {/* PiP (video only) */}
                  {type === 'video' && (
                    <ActionIcon variant="subtle" c="white" onClick={togglePiP}>
                      <IconPictureInPicture size={18} />
                    </ActionIcon>
                  )}

                  {/* Fullscreen (video only) */}
                  {type === 'video' && (
                    <ActionIcon variant="subtle" c="white" onClick={toggleFullscreen}>
                      {isFullscreen ? <IconMinimize size={18} /> : <IconMaximize size={18} />}
                    </ActionIcon>
                  )}
                </Group>
              </Group>
            </Box>
          )}

          {/* Buffering indicator */}
          {isBuffering && (
            <Box
              pos="absolute"
              top="50%"
              left="50%"
              style={{ transform: 'translate(-50%, -50%)' }}
            >
              <Text c="white" style={accessibilityStyles}>Loading...</Text>
            </Box>
          )}
        </Box>

        {/* Title for audio */}
        {type === 'audio' && title && (
          <Box p="sm">
            <Text size="sm" fw={500} style={accessibilityStyles}>{title}</Text>
          </Box>
        )}
      </Paper>
    );
  }
);

MediaPlayer.displayName = 'MediaPlayer';

export default MediaPlayer;
